<template>
    <div class="h-full">
        <Notification
            v-model="toast.show"
            :title="toast.title"
            :message="toast.message"
            :status="toast.status"
            :timeout="toast.timeout"
            :success="true"
        />
        <RouterView />
        <CommonDialog
            v-model="dialogStore.isOpen"
            :title="''"
            :content="dialogStore.content"
            :bind-properties="dialogStore.bindProperties"
        />
    </div>
</template>

<script setup>
import { onMounted, watch, watchEffect } from 'vue'
import { useIntegration } from '@/composables/useIntegration'
import { useAuthStore } from './stores/auth'
import { useClientStore } from './stores/client'
import { useSessionStore } from './stores/session'
const { login } = useIntegration()
const authStore = useAuthStore()
const clientStore = useClientStore()
import { useRouter, useRoute } from 'vue-router'
import { apiGet } from './util/api'
import Notification from './components/Notification.vue'
import { useToast } from './stores/toast'
import { useDialog } from './stores/dialog'
import CommonDialog from './components/common-dialog.vue'

const sessionStore = useSessionStore()
const router = useRouter()
const toast = useToast()
const dialogStore = useDialog()

onMounted(() => {
    // Check for theme URL parameter
    const urlParams = new URLSearchParams(window.location.search)
    const themeParam = urlParams.get('theme')

    if (themeParam) {
        // Set theme based on URL parameter
        if (themeParam === 'dark') {
            sessionStore.toggleMode(true)
            document.body.classList.add('dark')
        } else if (themeParam === 'light') {
            sessionStore.toggleMode(false)
            document.body.classList.remove('dark')
        }
    } else {
        // Use stored preference if no URL parameter
        document.body.classList.toggle('dark', sessionStore.mode === 'dark')
    }

    if (window.location.href.includes('my.geotab.com')) {
        geotab.addin.ofhMaintMap = function (api, state) {
            let currentUser = ''
            let geotab_credentials = null
            let geotab_credentials_expiry = null
            let geotab_user = null
            let geotab_username = null

            let fetchCredentialsPromise = null // Holds the promise for the in-flight credentials fetch for getGeotabCredentials method
            let fetchUserPromise = null // Holds the promise for the in-flight user fetch
            function geotabInitialize(api, state, initializeCallback, geotab_credentials) {
                getGeotabUser()
                    .then((user) => {
                        // $.i18n({
                        //     locale: user.language
                        // }).load({
                        //     'en': rootUrl + '/i18n/en.json',
                        //     'fr': rootUrl + '/i18n/fr.json',
                        //     'es': rootUrl + '/i18n/es.json'
                        // }).done(function() {
                        //     $('body').i18n();
                        //     $("#vehicle_search").select2({
                        //         width: '100%',
                        //         sorter: function(data) {
                        //             return data.sort(function(a, b) {
                        //                 return a.text.localeCompare(b.text);
                        //             })
                        //         },
                        //         placeholder: $.i18n("map-select-vehicle")
                        //     });
                        //     $("#vehicle_search").off('select2:select').on('select2:select', function(e) {
                        //         var id = e.params.data.id;
                        //         search_marker = null;
                        //         marker_layers = ["High", "Medium", "Low", "Ready", "Inactive"];
                        //         for (var i = 0; i < marker_layers.length; i++) {
                        //             for (var j = 0; j < device_markers[marker_layers[i]].length; j++) {
                        //                 if (id == device_markers[marker_layers[i]][j].options.deviceInfo.id) {
                        //                     search_marker = device_markers[marker_layers[i]][j];
                        //                     break;
                        //                 }
                        //             }
                        //         }
                        //         var latLngs = [search_marker.getLatLng()];
                        //         var markerBounds = L.latLngBounds(latLngs);
                        //         leafletMap.fitBounds(markerBounds);
                        //         search_marker.fire('click');
                        //     });
                        // });
                    })
                    .catch((error) => {
                        console.error('Error fetching Geotab user:', error)
                    })

                // When the user navigates to the page for the first time.
                initializeCallback()
            }
            async function getGeotabCredentials() {
                // Check if credentials are valid and not expired
                if (geotab_credentials_expiry && geotab_credentials_expiry > new Date().getTime()) {
                    console.log('Using cached Geotab credentials')
                    return geotab_credentials
                } else if (fetchCredentialsPromise) {
                    // If a fetch is already in progress, wait for it
                    console.log('Waiting for ongoing fetch to complete')
                    return fetchCredentialsPromise
                } else {
                    console.log('Fetching credentials from Geotab')
                    fetchCredentialsPromise = new Promise((resolve, reject) => {
                        api.getSession(function (session) {
                            try {
                                geotab_credentials = { ...session, path: window.location.hostname }
                                // Expire in 1 hour
                                geotab_credentials_expiry = new Date().getTime() + 60 * 60 * 1000
                                resolve(geotab_credentials)
                            } catch (error) {
                                reject(error)
                            }
                        })
                    }).finally(() => {
                        fetchCredentialsPromise = null // Reset after the promise settles
                    })

                    return fetchCredentialsPromise
                }
            }

            async function getGeotabUser() {
                // Ensure we have valid credentials
                const credentials = await getGeotabCredentials()

                // If user details are already cached, return them
                if (geotab_user !== null) {
                    console.log('Returning cached user details')
                    return geotab_user
                } else if (fetchUserPromise) {
                    // If a fetch is already in progress, wait for it
                    console.log('Waiting for ongoing fetch to complete')
                    return fetchUserPromise
                }

                // If not cached and no fetch is in progress, start fetching user details
                console.log('Fetching user from Geotab')
                fetchUserPromise = new Promise((resolve, reject) => {
                    api.call(
                        'Get',
                        {
                            typeName: 'User',
                            search: {
                                name: credentials.geotab_username // Use the username from credentials
                            }
                        },
                        function (result) {
                            if (result && result.length > 0) {
                                // Cache the fetched user details
                                geotab_user = result[0]
                                resolve(result[0]) // Resolve the promise with the fetched result
                            } else {
                                reject(new Error('No user found')) // Reject the promise if no user is found
                            }
                        }
                    )
                })
                    .then((result) => {
                        fetchUserPromise = null // Reset after successful fetch
                        return result
                    })
                    .catch((error) => {
                        fetchUserPromise = null // Reset on error as well
                        throw error // Rethrow the error to ensure it's handled upstream
                    })

                return fetchUserPromise
            }

            return {
                //More info here on initialize, focus and blur function here - https://my.geotab.com/sdk/#/addins
                initialize: function (api, state, initializeCallback) {
                    getGeotabCredentials()
                        .then((credentials) => {
                            geotab_username = credentials.userName

                            geotabInitialize(api, state, initializeCallback, credentials.path)
                        })
                        .catch((error) => {
                            console.error('Error fetching Geotab credentials:', error)
                        })
                },
                focus: function (api, state) {
                    getGeotabCredentials()
                        .then((credentials) => {
                            if (
                                new Date(authStore.state.expiryDate).valueOf() -
                                    new Date().valueOf() / 1000 >
                                60 * 60
                            ) {
                                console.log('return cached token')
                            } else {
                                login('geotab', credentials)
                            }
                        })
                        .catch((error) => {
                            console.error('Error fetching Geotab credentials:', error)
                        })
                },
                blur: function () {}
            }
        }
    }
})
watch(authStore.state, () => {
    if (!authStore.state.isLoggedIn) {
        if (!window.location.href.includes('my.geotab.com')) {
            router.push('/login')
        } else {
            window.location.reload()
        }
        clientStore.setCurrentClient(undefined)
    }
})
watch(
    () => clientStore.currentClient,
    () => {
        if (clientStore.currentClient) {
            apiGet(`/groups?client_id=${clientStore.currentClient?.id}`, authStore)
                .then((res) => {
                    clientStore.setGroups(res.groups)
                })
                .catch((err) => {})
        }
    },
    { immediate: true }
)
</script>
<!-- <script setup>
// import geotabScript from "./components/integrations/geotab/geotabScript.js"
</script> -->

<!-- <template>
    <header>
        <LogoSVG alt="Vite logo" class="logo" />


    <RouterView />
</template> -->
